import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { validatePassword } from '../utils/validation';
import {
  AuthShell,
  AuthIconBadge,
  AuthField,
  ShieldLockIcon,
  MailSendIcon,
  PersonIcon,
  MailIcon,
  LockIcon,
  EyeIcon,
  ArrowRightIcon,
  BackArrowIcon
} from './authShared';

export default function Register() {
  const { register, completeEmailVerification, resendEmailVerificationCode } = useContext(AuthContext);
  const [form, setForm] = useState({ username: '', name: '', lastname: '', email: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [agree, setAgree] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [error, setError] = useState('');
  const nav = useNavigate();

  // Email verification step, shown after the registration form is submitted
  // successfully. The account exists but is unusable until this code is
  // confirmed - that's what actually completes registration.
  const [pendingEmail, setPendingEmail] = useState(null);
  const [verificationSent, setVerificationSent] = useState(true);
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setInterval(() => {
        setResendCountdown(prev => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCountdown]);

  function setField(key) {
    return e => {
      const val = e.target.value;
      setForm(prev => ({ ...prev, [key]: val }));
      if (fieldErrors[key]) {
        setFieldErrors(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    };
  }

  function validateClientForm() {
    const errors = {};

    // Username validation
    const username = form.username.trim();
    if (!username) {
      errors.username = 'Username is required.';
    } else if (username.length < 4 || username.length > 32) {
      errors.username = 'Username must be between 4 and 32 characters.';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(username)) {
      errors.username = 'Username can only contain letters, numbers, underscore, hyphen, or dot.';
    }

    // First Name validation
    const name = form.name.trim();
    if (!name) {
      errors.name = 'First name is required.';
    } else if (name.length < 2 || name.length > 50) {
      errors.name = 'First name must be between 2 and 50 characters.';
    } else if (!/^[a-zA-Z\s'-]+$/.test(name)) {
      errors.name = 'First name must contain letters only.';
    }

    // Last Name validation
    const lastname = form.lastname.trim();
    if (!lastname) {
      errors.lastname = 'Last name is required.';
    } else if (lastname.length < 2 || lastname.length > 50) {
      errors.lastname = 'Last name must be between 2 and 50 characters.';
    } else if (!/^[a-zA-Z\s'-]+$/.test(lastname)) {
      errors.lastname = 'Last name must contain letters only.';
    }

    // Email validation
    const email = form.email.trim();
    if (!email) {
      errors.email = 'Email address is required.';
    } else if (!email.includes('@')) {
      errors.email = "Email must include an '@' symbol (e.g. name@example.com).";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address with a domain.';
    }

    // Password validation
    const passwordValidation = validatePassword(form.password);
    if (!passwordValidation.ok) {
      errors.password = passwordValidation.message;
    }

    // Policy checkbox agreement
    if (!agree) {
      errors.agree = 'You must accept the policy before registering.';
    }

    return errors;
  }

  async function submit(e) {
    e.preventDefault();
    const clientErrors = validateClientForm();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setError('');
      return;
    }

    setFieldErrors({});
    setError('');

    const fd = new FormData();
    Object.keys(form).forEach(k => fd.append(k, form[k].trim()));
    const res = await register(fd);
    if (res.ok) {
      nav('/');
    } else if (res.requiresEmailVerification) {
      setPendingEmail(res.email);
      setVerificationSent(res.verificationSent !== false);
      setError('');
    } else {
      const serverError = res.error || 'Register failed.';
      const lower = serverError.toLowerCase();
      const newFieldErrors = {};

      if (lower.includes('username') || res.field === 'username') {
        newFieldErrors.username = serverError;
      } else if (lower.includes('email') || res.field === 'email') {
        newFieldErrors.email = serverError;
      } else if (lower.includes('password') || res.field === 'password') {
        newFieldErrors.password = serverError;
      } else if (lower.includes('first name') || res.field === 'name') {
        newFieldErrors.name = serverError;
      } else if (lower.includes('last name') || res.field === 'lastname') {
        newFieldErrors.lastname = serverError;
      }

      setFieldErrors(newFieldErrors);
      // Only set top error banner if not mapped to a specific field
      if (Object.keys(newFieldErrors).length === 0) {
        setError(serverError);
      } else {
        setError('');
      }
    }
  }

  async function submitCode(e) {
    e.preventDefault();
    setVerifying(true);
    setVerifyError('');
    const res = await completeEmailVerification(pendingEmail, code.trim());
    setVerifying(false);
    if (res.ok) {
      nav('/');
    } else {
      setVerifyError(res.error || 'Verification failed.');
    }
  }

  async function handleResend() {
    if (resending || resendCountdown > 0) return;
    setResending(true);
    setVerifyError('');
    setResendMsg('');
    const res = await resendEmailVerificationCode(pendingEmail);
    setResending(false);
    if (res.ok) {
      setResendMsg('A new verification code has been sent to your email.');
      setResendCountdown(60);
    } else {
      // Backend email failure: DO NOT start countdown so user can retry immediately
      setResendCountdown(0);
      setVerifyError(res.error || 'Failed to send verification email. Please try again.');
    }
  }

  if (pendingEmail) {
    return (
      <AuthShell>
        <div className="auth-card">
          <AuthIconBadge><MailSendIcon /></AuthIconBadge>
          <h1 className="auth-card-title">Verify your email</h1>
          <p className="auth-card-subtitle">
            {verificationSent
              ? <>We sent a 6-digit code to <strong>{pendingEmail}</strong>. Enter it below to finish creating your account.</>
              : <>Your account was created, but we could not send a code to <strong>{pendingEmail}</strong>. Use resend below to try again.</>}
          </p>

          <form onSubmit={submitCode}>
            {verifyError && <div className="auth-alert">{verifyError}</div>}
            {resendMsg && <div className="auth-alert auth-alert-success">{resendMsg}</div>}

            <AuthField
              label="Verification code"
              icon={<LockIcon />}
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
            />

            <button type="submit" className="auth-submit-btn" disabled={verifying || code.length !== 6}>
              {verifying ? 'Verifying…' : <>Verify &amp; create account <ArrowRightIcon /></>}
            </button>
          </form>

          <div className="auth-resend-box">
            <button
              type="button"
              className="auth-link-btn"
              onClick={handleResend}
              disabled={resending || resendCountdown > 0}
              style={{ display: 'inline-block' }}
            >
              {resending
                ? 'Sending code…'
                : resendCountdown > 0
                  ? `Resend code in ${resendCountdown}s`
                  : "Didn't get a code? Resend"}
            </button>
          </div>

          <Link to="/login" className="auth-back-link">
            <BackArrowIcon /> Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="auth-card">
        <AuthIconBadge><ShieldLockIcon /></AuthIconBadge>
        <h1 className="auth-card-title">Create your account</h1>
        <p className="auth-card-subtitle">Join us and start trading securely</p>

        <form onSubmit={submit} noValidate>
          {error && <div className="auth-alert">{error}</div>}

          <AuthField
            label="Username"
            icon={<PersonIcon />}
            name="username"
            placeholder="Choose a username"
            value={form.username}
            onChange={setField('username')}
            error={fieldErrors.username}
            required
          />

          <div className="auth-field-pair">
            <AuthField
              label="First name"
              icon={<PersonIcon />}
              name="name"
              placeholder="First name"
              value={form.name}
              onChange={setField('name')}
              error={fieldErrors.name}
              required
            />
            <AuthField
              label="Last name"
              icon={<PersonIcon />}
              name="lastname"
              placeholder="Last name"
              value={form.lastname}
              onChange={setField('lastname')}
              error={fieldErrors.lastname}
              required
            />
          </div>

          <AuthField
            label="Email"
            icon={<MailIcon />}
            name="email"
            type="email"
            placeholder="Enter your email"
            value={form.email}
            onChange={setField('email')}
            error={fieldErrors.email}
            required
          />
          <p className="auth-hint-text">
            One account per email address. We'll send a verification code here.
          </p>

          <AuthField
            label="Password"
            icon={<LockIcon />}
            name="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a password"
            value={form.password}
            onChange={setField('password')}
            error={fieldErrors.password}
            required
            suffix={(
              <button
                type="button"
                className="auth-eye-btn"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide entered value' : 'Show entered value'}
              >
                <EyeIcon off={showPassword} />
              </button>
            )}
          />
          <p className="auth-hint-text">
            Must be at least 8 characters with an uppercase letter, a number, and a symbol.
          </p>

          <label className={`auth-checkbox auth-policy-row ${fieldErrors.agree ? 'auth-checkbox-error' : ''}`}>
            <input
              type="checkbox"
              checked={agree}
              onChange={e => {
                setAgree(e.target.checked);
                if (fieldErrors.agree) {
                  setFieldErrors(prev => {
                    const next = { ...prev };
                    delete next.agree;
                    return next;
                  });
                }
              }}
            />
            <span>
              I agree to the{' '}
              <button type="button" className="auth-link-btn" onClick={() => setShowPolicy(true)}>marketplace policy</button>
            </span>
          </label>
          {fieldErrors.agree && <span className="auth-checkbox-error-msg">{fieldErrors.agree}</span>}

          <button type="submit" className="auth-submit-btn">
            Create account <ArrowRightIcon />
          </button>
        </form>

        <p className="auth-bottom-text">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>

      {showPolicy && (
        <div className="modal-backdrop" onClick={() => setShowPolicy(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h4>Policy</h4>
            <p>By registering you confirm your details are correct. Follow our marketplace rules and be honest about listings.</p>
            <p>Buyers and sellers should use escrow for secure delivery of money and credentials.</p>
            <p>Off-platform contact info in listings is not allowed, and accounts must be ownership-verified before selling. <Link to="/policy" target="_blank" rel="noopener noreferrer">Read the full policy</Link>.</p>
            <button className="btn btn-secondary" type="button" onClick={() => setShowPolicy(false)}>Close</button>
          </div>
        </div>
      )}
    </AuthShell>
  );
}
